import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Palette, Save, Loader2, Eye, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { key: 'spec',    label: 'IT Spec Sheet', hint: 'Single-product institutional PDF' },
  { key: 'invoice', label: 'Tax Invoice',   hint: 'UAE VAT 5%, dual-currency' },
  { key: 'rfq',     label: 'RFQ',           hint: 'Request for Quotation' },
  { key: 'grn',     label: 'Goods Receipt', hint: 'GRN reconciliation note' },
];

const SUGGESTED_PALETTE = [
  '#0077B6', '#023E8A', '#0F172A', '#1E3A8A', '#0EA5E9',
  '#8B6914', '#B7791F', '#9F1239', '#7C2D12', '#065F46',
];

/**
 * BrandPreviewModal — live PDF preview + palette/logo editor per company.
 *
 * Props
 *   open: boolean
 *   company: { id, trading_name, legal_name, theme_color, logo_base64, … }
 *   token: JWT
 *   onClose()
 *   onSaved(updatedCompany)
 */
const BrandPreviewModal = ({ open, company, token, onClose, onSaved }) => {
  const { toast } = useToast();
  const [docType, setDocType] = useState('spec');
  const [themeColor, setThemeColor] = useState('#0077B6');
  const [logoBase64, setLogoBase64] = useState(null);  // overlay-only override (live preview)
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const lastBlobRef = useRef(null);
  const abortRef = useRef(null);

  // Reset state every time we open on a new company
  useEffect(() => {
    if (!open || !company) {
      // Also clear preview state on close to prevent flashing the previous PDF
      setPdfUrl(null);
      if (lastBlobRef.current) {
        try { URL.revokeObjectURL(lastBlobRef.current); } catch { /* noop */ }
        lastBlobRef.current = null;
      }
      return;
    }
    setDocType('spec');
    setThemeColor(company.theme_color || '#0077B6');
    setLogoBase64(null);
    setPdfUrl(null);
  }, [open, company]);

  // Debounced live-render whenever doc/color changes
  useEffect(() => {
    if (!open || !company) return undefined;
    const id = setTimeout(() => { fetchPreview(); }, 300);
    return () => clearTimeout(id);
  }, [open, company, docType, themeColor, logoBase64]);

  // Revoke last blob URL on unmount or new render
  useEffect(() => () => {
    if (lastBlobRef.current) {
      try { URL.revokeObjectURL(lastBlobRef.current); } catch { /* noop */ }
    }
  }, []);

  const fetchPreview = async () => {
    if (!company?.id) return;
    // Abort any in-flight preview so the colour-picker drag doesn't queue up
    // dozens of server renders.
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/companies/${company.id}/brand-preview/${docType}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ theme_color: themeColor, logo_base64: logoBase64 || undefined }),
          signal: ctrl.signal,
        },
      );
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      if (lastBlobRef.current) {
        try { URL.revokeObjectURL(lastBlobRef.current); } catch { /* noop */ }
      }
      const url = URL.createObjectURL(blob);
      lastBlobRef.current = url;
      setPdfUrl(url);
    } catch (e) {
      if (e?.name === 'AbortError') return;  // expected — superseded by a newer render
      toast({ variant: 'destructive', title: 'Preview failed', description: String(e.message || e) });
    } finally {
      if (abortRef.current === ctrl) setLoading(false);
    }
  };

  const onLogoFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoBase64(reader.result);  // data:image/png;base64,...
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!company?.id) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/companies/${company.id}/branding`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          theme_color: themeColor,
          ...(logoBase64 ? { logo_base64: logoBase64 } : {}),
        }),
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const out = await r.json();
      toast({ title: 'Branding saved', description: `${company.trading_name || company.legal_name} · ${out.theme_color}` });
      onSaved?.({ ...company, theme_color: out.theme_color });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: String(e.message || e) });
    } finally {
      setSaving(false);
    }
  };

  const palette = useMemo(() => SUGGESTED_PALETTE, []);
  if (!open || !company) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-stretch p-4"
      onClick={onClose}
      data-testid="brand-preview-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-auto flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-inner"
              style={{ backgroundColor: themeColor }}
            >
              <Palette className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF6B35]">
                Brand Preview
              </div>
              <h2 className="text-lg font-extrabold text-[#0F172A]">
                {company.trading_name || company.legal_name}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="brand-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="brand-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-0 flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="bg-slate-50 border-r border-slate-100 p-5 overflow-y-auto flex flex-col gap-5">
            {/* Document picker */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#6C757D] mb-2">Document type</div>
              <div className="space-y-1.5">
                {DOC_TYPES.map((d) => (
                  <button
                    key={d.key}
                    onClick={() => setDocType(d.key)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center justify-between transition ${
                      docType === d.key
                        ? 'bg-[#0F172A] text-white shadow-sm'
                        : 'bg-white text-[#0F172A] border border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`brand-doc-${d.key}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold truncate">{d.label}</div>
                      <div className={`text-[10px] ${docType === d.key ? 'text-slate-200' : 'text-[#6C757D]'} truncate`}>
                        {d.hint}
                      </div>
                    </div>
                    {docType === d.key && <Eye className="w-3.5 h-3.5 flex-shrink-0 ml-2" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Palette */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#6C757D] mb-2">Theme colour</div>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="color"
                  value={themeColor}
                  onChange={(e) => setThemeColor(e.target.value)}
                  className="w-10 h-10 rounded-md border border-slate-200 cursor-pointer"
                  data-testid="brand-color-picker"
                  title="Pick any colour"
                />
                <input
                  type="text"
                  value={themeColor}
                  onChange={(e) => setThemeColor(e.target.value)}
                  placeholder="#0077B6"
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono uppercase"
                  data-testid="brand-color-hex"
                />
              </div>
              <div className="grid grid-cols-5 gap-2">
                {palette.map((c) => (
                  <button
                    key={c}
                    onClick={() => setThemeColor(c)}
                    className={`h-8 rounded-md border-2 transition ${themeColor.toLowerCase() === c.toLowerCase() ? 'border-[#0F172A] scale-110' : 'border-transparent hover:border-slate-300'}`}
                    style={{ backgroundColor: c }}
                    title={c}
                    data-testid={`brand-color-suggest-${c.slice(1)}`}
                  />
                ))}
              </div>
            </div>

            {/* Logo upload */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#6C757D] mb-2">Logo (PNG with transparency)</div>
              <label className="flex items-center justify-center w-full px-3 py-2 border-2 border-dashed border-slate-200 rounded-md text-xs font-bold text-[#0F172A] hover:bg-white cursor-pointer">
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-[#FF6B35]" />
                Upload to preview
                <input
                  type="file"
                  accept="image/png"
                  className="hidden"
                  onChange={(e) => onLogoFile(e.target.files?.[0])}
                  data-testid="brand-logo-upload"
                />
              </label>
              <div className="text-[10px] text-[#6C757D] mt-1.5 leading-relaxed">
                The PDF auto-recolors dark logos to white when the band is dark. Click <b>Save changes</b> to persist.
              </div>
            </div>

            {/* Identity readout (read-only) */}
            <div className="text-[11px] text-[#6C757D] leading-relaxed mt-auto pt-4 border-t border-slate-200">
              <div><span className="font-bold text-[#0F172A]">TRN:</span> {company.trn || '—'}</div>
              <div><span className="font-bold text-[#0F172A]">Email:</span> {company.email || '—'}</div>
              <div><span className="font-bold text-[#0F172A]">Phone:</span> {company.phone || '—'}</div>
              <div><span className="font-bold text-[#0F172A]">Website:</span> {company.website || '—'}</div>
            </div>
          </div>

          {/* PDF preview */}
          <div className="bg-slate-200 relative flex items-center justify-center overflow-hidden">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                <Loader2 className="w-7 h-7 text-[#0F172A] animate-spin" />
              </div>
            )}
            {pdfUrl ? (
              <iframe
                title={`brand-preview-${docType}`}
                src={pdfUrl}
                className="w-full h-full border-0"
                data-testid="brand-preview-iframe"
              />
            ) : !loading && (
              <div className="text-center text-[#6C757D]">
                <div className="text-sm">Pick a document type to render a preview.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPreviewModal;
